import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const TimeSaved = () => {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/time-saved").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const rows = data.items.filter((i) => i.count > 0);
  const maxHours = Math.max(...data.history.map((h) => h.hours), 1);
  const monthLabel = (m) =>
    new Date(`${m}-01T00:00:00`).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", {
      month: "short",
    });

  return (
    <div className="card-flat p-6" data-testid="time-saved-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
            <Clock className="h-4 w-4 text-indigo-600" /> {t("time.title")}
          </h2>
          <p className="mt-1 text-xs text-slate-500">{t("time.subtitle")}</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("time.thisMonth")}
          </p>
          <p className="font-mono-num text-3xl font-semibold text-slate-900" data-testid="time-saved-hours">
            {data.hours} <span className="text-base font-medium text-slate-500">{t("time.hours")}</span>
          </p>
          <p className="text-xs text-slate-500" data-testid="time-saved-6m">
            {t("time.sixMonths")}: <span className="font-mono-num">{data.hours_6m}</span>{" "}
            {t("time.hours")}
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="time-saved-empty">
          {t("time.empty")}
        </p>
      ) : (
        <ul className="mt-5 space-y-2" data-testid="time-saved-list">
          {rows.map((item) => (
            <li
              key={item.key}
              className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm last:border-0"
              data-testid={`time-row-${item.key}`}
            >
              <span className="min-w-0 truncate">
                {t(`time.${item.key}`)}{" "}
                <span className="hidden text-xs text-slate-400 sm:inline">
                  · {item.count} × {item.minutes_each} {t("time.each")}
                </span>
              </span>
              <span className="font-mono-num shrink-0 font-semibold">
                {Math.round((item.minutes / 60) * 10) / 10} {t("time.hours")}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex items-end gap-2" data-testid="time-saved-history">
        {data.history.map((h) => (
          <div key={h.month} className="flex flex-1 flex-col items-center gap-1">
            <div
              className="w-full rounded-t bg-indigo-600/80 transition-all duration-500"
              style={{ height: `${Math.max((h.hours / maxHours) * 72, 3)}px` }}
              title={`${h.hours} ${t("time.hours")}`}
            />
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              {monthLabel(h.month)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeSaved;
